import { safeUUID, isUUID } from './isUUID'
import { supabaseClient } from '../supabase'

/**
 * Sanitizes entry values to ensure proper null handling and UUID validation
 * @param {Object} entry - Single assignment entry
 * @returns {Object} Sanitized entry
 */
const sanitizeEntry = (entry) => {
  const sanitized = { ...entry }
  
  // All UUID fields from assignation_logs table
  const uuidFields = [
    'lead_id',
    'pubeur_id',
    'lead_previous_pubeur_id',
    'current_year_id',
    'author'
  ]
  
  uuidFields.forEach(field => {
    if (field in sanitized) {
      const value = sanitized[field]
      
      // Convert string "null"/"undefined" or actual null/undefined/empty to null
      if (!value || value === "null" || value === "undefined" || value === "") {
        sanitized[field] = null
      } else {
        // Process valid UUID strings
        const processedUUID = safeUUID(value)
        // If safeUUID returns "null" string or falsy value, set to null
        sanitized[field] = (processedUUID === "null" || !processedUUID) ? null : processedUUID
      }
    }
  })
  
  return sanitized
}

/**
 * Logs assignment entries to the database
 * @param {Array<Object>} entries - Array of assignment objects to log
 * @returns {Promise<{success: boolean, message?: string, error?: string}>}
 */
const logAssignation = async (entries) => {
  // Improved validation
  if (!entries || !Array.isArray(entries) || entries.length === 0) {
    return {
      error: 'entries must be a non-empty array'
    }
  }
  
  try {
    // Sanitize all entries
    const sanitizedEntries = entries.map(sanitizeEntry)
    
    // Validate required fields
    const requiredFields = ['lead_id', 'author']
    for (let i = 0; i < sanitizedEntries.length; i++) {
      const entry = sanitizedEntries[i]
      
      for (const field of requiredFields) {
        if (!entry[field]) {
          return {
            error: `Entry at index ${i} is missing required field: ${field}`
          }
        }
      }
      
      // Optional: Additional validation for pubeur_id if it's not null
      if (entry['pubeur_id'] !== null && entry['pubeur_id'] !== undefined) {
        // If pubeur_id is provided, it should be a valid UUID
        if ( ! isUUID(entry['pubeur_id'] ) ) {
          return {
            error: `Entry at index ${i} has invalid pubeur_id format: ${entry['pubeur_id']}`
          }
        }
      }
    }
    
    const { error } = await supabaseClient
      .from('assignation_logs')
      .insert(sanitizedEntries)
   
    if (error) {
      // Log error to monitoring table
      await supabaseClient
        .from('suivi_bilans')
        .insert({
          comment: `Error saving assignation logs: ${error.message}`,
          values: JSON.stringify({
            entries_count: sanitizedEntries.length,
            error: error.message,
            timestamp: new Date().toISOString()
          })
        })
        .catch(suiviError => {
          console.error('Failed to log to suivi_bilans:', suiviError)
        })
     
      return {
        error: `Failed to insert assignation logs: ${error.message}`
      }
    }
    
    return { success: true }
    
  } catch (error) {
    console.error('Exception in logAssignation:', error)
    return {
      error: `Exception in logAssignation: ${error.message || error}`
    }
  }
}

/**
 * The history row of a lasting change of holder. `previousHolderId` matters:
 * the past-contacts feature finds « a contact I held this year » only through
 * these rows (docs/project-specs/past-contacts.md), so a row that forgets who
 * lost the contact makes that person lose it for good.
 */
export const permanentAssignationLog = ({ authorId, leadId, newHolderId, previousHolderId, yearId }) => ({
  author: authorId,
  pubeur_id: newHolderId,
  lead_id: leadId,
  temporary: false,
  lead_previous_pubeur_id: previousHolderId ?? null,
  duration: null,
  current_year_id: yearId
})

/**
 * The history rows of a bulk change of holder: one per lead whose holder
 * changes, each naming who held it before. `leads` are read just before the
 * update, as `{ id, assigned_pubeur }`.
 */
export const holderChangeLogs = ({ leads, newHolderId, authorId, yearId }) => leads
  .filter((lead) => lead.assigned_pubeur !== newHolderId)
  .map((lead) => permanentAssignationLog({
    authorId,
    leadId: lead.id,
    newHolderId,
    previousHolderId: lead.assigned_pubeur,
    yearId
  }))

export default logAssignation